/**
 * Central, framework-agnostic translator that turns any API / network error into a
 * short, friendly, layman-readable message suitable for showing directly to users.
 *
 * Resolution order:
 *   1. A meaningful message from the backend response body (most are already friendly).
 *   2. The kind of network failure (offline / timeout) when there is no HTTP response.
 *   3. A message mapped from the HTTP status code.
 *   4. The caller-provided fallback.
 *
 * Technical/internal strings (stack traces, Mongoose cast errors, "Request failed with
 * status code 404", raw network codes, JSON blobs, etc.) are never shown to users.
 */

const TECHNICAL_MESSAGE =
  /(cast to objectid|objectid failed|validation failed:|buffering timed out|econnrefused|econnreset|enotfound|eai_again|etimedout|socket hang up|getaddrinfo|network error|request failed with status|xmlhttprequest|jwt (malformed|expired)|jsonwebtoken|^\s*[[{<]|undefined is not|cannot read propert|is not a function|\bnull\b.*\bproperty\b)/i;

/** True when a string looks like an internal/technical message we should hide from users. */
function isTechnical(message?: string | null): boolean {
  if (!message) return true;
  const m = String(message).trim();
  if (!m) return true;
  return TECHNICAL_MESSAGE.test(m);
}

/** Extract a usable backend message from an axios-style error response body. */
function backendMessage(error: any): string | undefined {
  const data = error?.response?.data;
  if (!data) return undefined;
  if (typeof data === 'string') return data;
  if (typeof data.message === 'string' && data.message.trim()) return data.message;
  if (typeof data.error === 'string' && data.error.trim()) return data.error;
  return undefined;
}

export interface FriendlyMessageOptions {
  /** Used when nothing more specific can be determined. */
  fallback?: string;
  /** Override the message shown for a 404 (e.g. "No donor found with this CNIC"). */
  notFound?: string;
}

const STATUS_MESSAGES: Record<number, string> = {
  400: 'Some of the information looks invalid. Please review your input and try again.',
  401: 'Your session has expired. Please sign in again.',
  403: "You don't have permission to do this.",
  404: 'We couldn’t find what you were looking for.',
  408: 'The request timed out. Please try again.',
  409: 'This conflicts with existing data — it may already exist.',
  413: 'The information you’re sending is too large.',
  415: 'That file or request format isn’t supported.',
  422: 'Some of the information looks invalid. Please review your input and try again.',
  429: 'Too many attempts. Please wait a moment and try again.',
};

const SERVER_DOWN = 'Something went wrong on our end. Please try again in a moment.';
const OFFLINE =
  'Unable to reach the server. Please check your internet connection and try again.';
const TIMEOUT = 'The request took too long to respond. Please try again.';

/**
 * Convert an unknown error into a friendly, user-readable message.
 * @param error    Any thrown value (axios error, Error, string, etc.)
 * @param options  A fallback string, or an options object.
 */
export function friendlyApiMessage(
  error: any,
  options?: string | FriendlyMessageOptions
): string {
  const opts: FriendlyMessageOptions =
    typeof options === 'string' ? { fallback: options } : options || {};
  const fallback = opts.fallback || 'Something went wrong. Please try again.';

  if (error == null) return fallback;
  if (typeof error === 'string') return isTechnical(error) ? fallback : error;

  const response = error?.response;
  const status: number | undefined = response?.status;

  // No HTTP response → offline, timeout, or a friendly message already thrown by our SDK.
  if (!response) {
    const code = error?.code;
    const rawMsg: string = error?.message || '';
    if (code === 'ECONNABORTED' || /timeout/i.test(rawMsg)) return TIMEOUT;
    if (code === 'ERR_NETWORK' || error?.request || /network/i.test(rawMsg)) return OFFLINE;
    // A message our own code already produced (and that isn't technical) is safe to show.
    if (!isTechnical(rawMsg)) return rawMsg;
    return fallback;
  }

  // Prefer a meaningful, non-technical backend message.
  const backend = backendMessage(error);
  if (backend && !isTechnical(backend)) return backend;

  if (status === 404 && opts.notFound) return opts.notFound;
  if (typeof status === 'number') {
    if (STATUS_MESSAGES[status]) return STATUS_MESSAGES[status];
    if (status >= 500) return SERVER_DOWN;
  }

  return fallback;
}

export default friendlyApiMessage;
